const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const initializedForms = new WeakSet();
const initializedMedia = new WeakSet();
const pageWrapper = document.querySelector('.page-wrapper');

function loadHoverImage(event) {
  if (!window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches) return;
  if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
  const card = event.target instanceof Element ? event.target.closest('.claro-product-card') : null;
  const image = card?.querySelector('[data-claro-hover-image]');
  if (!(image instanceof HTMLImageElement) || !image.dataset.src) return;
  image.addEventListener('load', () => image.classList.add('is-loaded'), { once: true });
  image.srcset = image.dataset.srcset;
  image.src = image.dataset.src;
  delete image.dataset.src;
  delete image.dataset.srcset;
}

document.addEventListener('pointerover', loadHoverImage);
document.addEventListener('focusin', loadHoverImage);

let revealObserver;

function setupRevealObserver() {
  if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

  revealObserver = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );
}

function initializeReveals(root = document) {
  const reveals = root.querySelectorAll('[data-reveal]');

  for (const reveal of reveals) {
    reveal.querySelectorAll('[data-reveal-item]').forEach((item, index) => {
      item.style.setProperty('--reveal-index', Math.min(index, 8));
    });

    if (reducedMotion.matches || !revealObserver) {
      reveal.classList.add('is-visible');
    } else {
      revealObserver.observe(reveal);
    }
  }
}

function initializeMedia(root = document) {
  const mediaSelectors = [
    '.claro-hero__media',
    '.claro-product-card__image',
    '.claro-collection-feature__media',
    '.claro-collection-card__image',
    '.claro-article-card__image',
    '.claro-article__image',
    '.claro-about-hero__visual',
    '.claro-about-purpose__visual',
    '.claro-collection-hero__image',
  ].join(',');

  for (const media of root.querySelectorAll(mediaSelectors)) {
    if (initializedMedia.has(media)) continue;

    const primaryImage = media.querySelector('img');
    if (!(primaryImage instanceof HTMLImageElement)) continue;

    initializedMedia.add(media);

    // Critical images should paint immediately instead of waiting for the
    // progressive-loading overlay and fade transition.
    if (primaryImage.loading !== 'lazy') continue;

    media.classList.add('claro-media-loading');

    const markLoaded = () => {
      media.classList.add('is-loaded');
    };

    if (primaryImage.complete) {
      markLoaded();
      continue;
    }

    primaryImage.addEventListener('load', markLoaded, { once: true });
    primaryImage.addEventListener('error', markLoaded, { once: true });
    window.setTimeout(markLoaded, 1800);
  }
}

function initializeForms(root = document) {
  for (const form of root.querySelectorAll('.claro-form, .claro-form-inline')) {
    if (initializedForms.has(form)) continue;
    initializedForms.add(form);

    form.addEventListener(
      'invalid',
      (event) => {
        const field = event.target;
        if (!(field instanceof HTMLElement)) return;
        field.classList.add('has-error');
        field.setAttribute('aria-invalid', 'true');
      },
      true
    );

    form.addEventListener('input', (event) => {
      const field = event.target;
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
      if (!field.validity.valid) return;
      field.classList.remove('has-error');
      field.removeAttribute('aria-invalid');
    });

    form.addEventListener('submit', () => {
      if (!form.checkValidity()) return;
      form.classList.add('is-submitting');
      form.setAttribute('aria-busy', 'true');
    });
  }

  const successMessage = root.querySelector('.claro-form-message[role="status"]');
  if (successMessage instanceof HTMLElement) successMessage.focus({ preventScroll: false });
}

function initializeHeader() {
  const header = document.querySelector('.claro-header');
  if (!header) return;

  let scheduled = false;
  let isScrolled;
  let headerResizeObserver;

  const syncHeaderHeight = (height) => {
    if (!Number.isFinite(height) || height <= 0) return;
    document.documentElement.style.setProperty('--claro-header-current-height', `${Math.ceil(height)}px`);
  };

  const updateHeaderHeight = () => {
    syncHeaderHeight(header.getBoundingClientRect().height);
  };

  const updateHeader = () => {
    const scrollPosition = Math.max(pageWrapper instanceof HTMLElement ? pageWrapper.scrollTop : 0, window.scrollY);
    const nextScrolled = scrollPosition > 16;
    if (nextScrolled !== isScrolled) {
      header.classList.toggle('is-scrolled', nextScrolled);
      isScrolled = nextScrolled;
      if (!headerResizeObserver) updateHeaderHeight();
    }
    scheduled = false;
  };

  const scheduleHeaderUpdate = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(updateHeader);
  };

  window.addEventListener(
    'scroll',
    scheduleHeaderUpdate,
    { passive: true }
  );
  pageWrapper?.addEventListener('scroll', scheduleHeaderUpdate, { passive: true });
  updateHeaderHeight();

  if ('ResizeObserver' in window) {
    headerResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const borderBox = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
      syncHeaderHeight(borderBox?.blockSize ?? entry.contentRect.height);
    });
    headerResizeObserver.observe(header);
  }

  updateHeader();

  const navigation = header.querySelector('.claro-mobile-nav');
  const cartTrigger = header.querySelector('.claro-header__cart-trigger');
  const cartDialog = document.querySelector('#cart-drawer dialog');

  if (cartTrigger && cartDialog) {
    const syncCartState = () => {
      const cartIsOpen = cartDialog.hasAttribute('open');
      cartTrigger.setAttribute('aria-expanded', String(cartIsOpen));
      document.body.classList.toggle('claro-cart-open', cartIsOpen);
    };
    new MutationObserver(syncCartState).observe(cartDialog, { attributes: true, attributeFilter: ['open'] });
    syncCartState();
  }

  if (!(navigation instanceof HTMLDetailsElement)) return;

  const summary = navigation.querySelector('summary');
  const backdrop = navigation.querySelector('.claro-mobile-nav__backdrop');
  const links = [...navigation.querySelectorAll('.claro-mobile-nav__panel a')];

  const closeNavigation = ({ restoreFocus = false } = {}) => {
    if (!navigation.open) return;
    navigation.open = false;
    if (restoreFocus && summary instanceof HTMLElement) summary.focus();
  };

  navigation.addEventListener('toggle', () => {
    document.body.classList.toggle('claro-menu-open', navigation.open);
    summary?.setAttribute('aria-expanded', String(navigation.open));
    summary?.setAttribute(
      'aria-label',
      navigation.open ? navigation.dataset.menuOpenLabel : navigation.dataset.menuClosedLabel
    );
  });

  backdrop?.addEventListener('click', () => closeNavigation({ restoreFocus: true }));
  links.forEach((link) => link.addEventListener('click', () => closeNavigation()));

  document.addEventListener('keydown', (event) => {
    if (!navigation.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeNavigation({ restoreFocus: true });
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = [summary, ...links].filter((element) => element instanceof HTMLElement);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  });

  window.addEventListener('resize', () => {
    scheduleHeaderUpdate();
    if (!headerResizeObserver) updateHeaderHeight();
    if (window.matchMedia('(min-width: 990px)').matches) closeNavigation();
  });
}

function initializeReadingProgress() {
  const article = document.querySelector('[data-reading-progress]');
  const progress = document.querySelector('.claro-reading-progress__bar');
  if (!(article instanceof HTMLElement) || !(progress instanceof HTMLElement)) return;

  let scheduled = false;
  const updateProgress = () => {
    const rect = article.getBoundingClientRect();
    const distance = article.offsetHeight - window.innerHeight;
    const value = distance <= 0 ? 1 : Math.min(1, Math.max(0, -rect.top / distance));
    progress.style.setProperty('--reading-progress', value.toFixed(4));
    scheduled = false;
  };

  const scheduleUpdate = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(updateProgress);
  };

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  pageWrapper?.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);
  updateProgress();
}

function initialize(root = document) {
  initializeReveals(root);
  initializeMedia(root);
  initializeForms(root);
}

setupRevealObserver();
initialize();
initializeHeader();
initializeReadingProgress();
document.documentElement.classList.add('claro-enhanced');

document.addEventListener('shopify:section:load', (event) => initialize(event.target));
